import {exhaustive} from '@freckle/exhaustive'

export type Result<T> =
  | {status: 'success'; data: T}
  | {status: 'error'; error: Error}
  | {status: 'pending'}

export function resultMap<A, B>(ra: Result<A>, f: (a: A) => B): Result<B> {
  switch (ra.status) {
    case 'error':
      return ra
    case 'pending':
      return ra
    case 'success':
      return {status: 'success', data: f(ra.data)}
    default:
      exhaustive(ra)
  }
}

export function resultThen<A, B>(ra: Result<A>, f: (a: A) => Result<B>): Result<B> {
  switch (ra.status) {
    case 'error':
      return ra
    case 'pending':
      return ra
    case 'success':
      return f(ra.data)
    default:
      exhaustive(ra)
  }
}

export function resultArraySequence<A>(rs: Array<Result<A>>): Result<Array<A>> {
  const data = []
  for (const r of rs) {
    if (r.status === 'error') return r
    if (r.status === 'pending') return r
    data.push(r.data)
  }
  return {status: 'success', data}
}

export function resultData<T>(result: Result<T>): T | undefined {
  switch (result.status) {
    case 'success':
      return result.data
    default:
      return
  }
}

export function resultError<T>(result: Result<T>): Error | undefined {
  switch (result.status) {
    case 'error':
      return result.error
    default:
      return
  }
}

type UnwrapResults<T> = {
  [K in keyof T]: T[K] extends Result<infer U> ? U : never
}

export function resultRecordSequence<T extends {[K in keyof T]: Result<UnwrapResults<T>[K]>}>(
  record: T,
): Result<UnwrapResults<T>> {
  const data: Record<string, unknown> = {}
  for (const key of Object.keys(record)) {
    const r = record[key as keyof T]
    if (r.status === 'error') return r as Result<UnwrapResults<T>>
    if (r.status === 'pending') return r as Result<UnwrapResults<T>>
    data[key] = r.data
  }
  return {status: 'success', data: data as UnwrapResults<T>}
}
